import { APIResponse, SBModel } from '@supplement-bacon/react-toolkit/models'
import { once } from 'lodash'
import {
  createContext,
  Dispatch,
  PropsWithChildren,
  ReactNode,
  SetStateAction,
  useContext,
  useEffect,
  useState,
} from 'react'
import * as LocalStorageHelper from '../localStorageHelper'
import {
  forgotPassword as forgotPasswordAPI,
  getLoggedUserInfos as getLoggedUserInfosAPI,
  login as loginAPI,
  logout as logoutAPI,
  register as registerAPI,
  resetPassword as resetPassworddAPI,
  updateLoggedUserInfos,
} from '../requets'

type AuthContextProps<Authenticable extends SBModel, RegisterData> = {
  currentUser: Authenticable | undefined
  isLogged: boolean | undefined
  setIsLogged: Dispatch<SetStateAction<boolean | undefined>>
  register: (data: RegisterData) => Promise<void>
  login: (email: string, password: string) => Promise<void>
  logout: () => void
  updateUser: (user: Authenticable) => Promise<boolean>
  forgotPassword: (email: string) => Promise<void>
  resetPassword: (
    email: string,
    token: string,
    password: string
  ) => Promise<void>
  getLoggedUserInfos: () => Promise<Authenticable | undefined>
  renderLoadingLayout: () => ReactNode
}

const initAuthContextPropsState = <
  Authenticable extends SBModel,
  RegisterData,
>(): AuthContextProps<Authenticable, RegisterData> => ({
  currentUser: undefined,
  isLogged: undefined,
  setIsLogged: () => {},
  register: () => Promise.resolve(),
  login: () => Promise.resolve(),
  logout: () => {},
  updateUser: ({}) => new Promise<boolean>(() => false),
  forgotPassword: () => Promise.resolve(),
  resetPassword: () => Promise.resolve(),
  getLoggedUserInfos: () => Promise.resolve(undefined),
  renderLoadingLayout: () => <></>,
})

const createAuthContext = once(
  <Authenticable extends SBModel, RegisterData>() =>
    createContext<AuthContextProps<Authenticable, RegisterData>>(
      initAuthContextPropsState<Authenticable, RegisterData>()
    )
)
const useAuth = <Authenticable extends SBModel, RegisterData>() =>
  useContext(createAuthContext<Authenticable, RegisterData>())

interface AuthProviderProps<Authenticable extends SBModel> {
  loadingLayout: ReactNode
  urls: {
    loggedUserInfos: string
    login: string
    logout: string
    register: string
    forgotPassword: string
    resetPassword: string
  }
  afterRegistration?: (user: Authenticable) => Promise<void>
  loggedIn?: (user: Authenticable) => Promise<void>
  loggedOut?: () => Promise<void>
}
const AuthProvider = <
  Authenticable extends SBModel,
  RegisterData = Record<string, any>,
>({
  loadingLayout,
  urls,
  children,
  afterRegistration,
  loggedIn,
  loggedOut,
}: PropsWithChildren<AuthProviderProps<Authenticable>>) => {
  const AuthContext = createAuthContext<Authenticable, RegisterData>()

  const [isLogged, setIsLogged] = useState<boolean | undefined>(undefined)
  const [currentUser, setCurrentUser] = useState<Authenticable | undefined>(
    undefined
  )

  const updateCurrentUser = (user: Authenticable | undefined) => {
    setCurrentUser(user)
    if (user) {
      LocalStorageHelper.setUserID(user.id)
    } else {
      LocalStorageHelper.removeUserID()
    }
  }

  const register = async <T,>(data: T) => {
    try {
      await registerAPI(urls.register, data)
      const authenticable = await getLoggedUserInfos()

      // Execute callback
      if (afterRegistration) {
        await afterRegistration(authenticable)
      }

      setIsLogged(true)
    } catch (error) {
      setIsLogged(false)
      throw error
    }
  }

  const login = async (email: string, password: string) => {
    try {
      await loginAPI(urls.login, email, password)
      const authenticable = await getLoggedUserInfos()

      // Execute callback
      if (loggedIn) {
        await loggedIn(authenticable)
      }

      setIsLogged(true)
    } catch (error) {
      setIsLogged(false)
      throw error
    }
  }

  const getLoggedUserInfos = async (): Promise<Authenticable> => {
    try {
      const { data: userResponse } = await getLoggedUserInfosAPI<
        APIResponse<Authenticable>
      >(urls.loggedUserInfos)
      updateCurrentUser(userResponse.data)
      return userResponse.data
    } catch (error) {
      setIsLogged(false)
      updateCurrentUser(undefined)
      throw error
    }
  }

  const forgotPassword = async (email: string) => {
    try {
      await forgotPasswordAPI(urls.forgotPassword, email)
    } catch (error) {
      throw error
    }
  }

  const resetPassword = async (
    email: string,
    token: string,
    password: string
  ) => {
    try {
      await resetPassworddAPI(urls.resetPassword, email, token, password)
    } catch (error) {
      throw error
    }
  }

  const logout = () => {
    logoutAPI(urls.logout).finally(async () => {
      // Local
      setIsLogged(false)
      updateCurrentUser(undefined)

      // Execute callback
      try {
        if (loggedOut) {
          await loggedOut()
        }
      } catch (error) {
        console.error('loggedOut callback failed', error)
        window.location.href = '/auth/login'
      }
    })
  }

  const updateUser = (user: Authenticable) =>
    updateLoggedUserInfos(urls.loggedUserInfos, user).then(async (_) => {
      await getLoggedUserInfos()
      return true
    })

  useEffect(() => {
    if (isLogged !== undefined) return
    const localUserId = LocalStorageHelper.getUserID()
    setIsLogged(localUserId !== undefined)
  }, [])

  if (isLogged === undefined) {
    return loadingLayout
  }

  return (
    <AuthContext.Provider
      value={{
        currentUser,
        isLogged,
        setIsLogged,
        register,
        login,
        logout,
        updateUser,
        forgotPassword,
        resetPassword,
        getLoggedUserInfos,
        renderLoadingLayout: () => loadingLayout,
      }}
    >
      {children}
    </AuthContext.Provider>
  )
}

export { AuthProvider, useAuth }
